import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  Info,
  AttachMoney,
  ShowChart,
  Speed,
  Security,
} from '@mui/icons-material';
import { GlobalData } from '../config/api';
import {
  formatLargeNumber,
  formatPercentage,
  getColorByValue,
} from '../utils/tradingUtils';
import { fetchWithRetry, handleApiError } from '../utils/apiUtils';
import { colors, typography, spacing, borderRadius, componentStyles } from '../styles/designSystem';

const useStyles = makeStyles((theme) => ({
  dashboard: {
    background: colors.gradients.hero,
    padding: `${spacing['4xl']} 0`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(230, 0, 122, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
  },
  
  container: {
    ...componentStyles.contentContainer,
    position: 'relative',
    zIndex: 2,
  },
  
  title: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.black,
    fontSize: typography.fontSize['4xl'],
    color: colors.text.primary,
    marginBottom: spacing['3xl'],
    textAlign: 'center',
    letterSpacing: typography.letterSpacing.tight,
    background: colors.gradients.primary,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    [theme.breakpoints.up('xl')]: {
      fontSize: typography.fontSize['5xl'],
      marginBottom: spacing['4xl'],
    },
    [theme.breakpoints.up('xxl')]: {
      fontSize: typography.fontSize['6xl'],
    },
  },
  
  card: {
    ...componentStyles.glassCard,
    height: '100%',
    padding: spacing.xl,
    [theme.breakpoints.up('lg')]: {
      padding: spacing['2xl'],
    },
    [theme.breakpoints.up('xl')]: {
      padding: spacing['3xl'],
    },
  },
  
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.lg,
    '& h6': {
      color: colors.text.primary,
      fontWeight: typography.fontWeight.semiBold,
      fontSize: typography.fontSize.lg,
      [theme.breakpoints.up('lg')]: {
        fontSize: typography.fontSize.xl,
      },
      [theme.breakpoints.up('xl')]: {
        fontSize: typography.fontSize['2xl'],
      },
    },
  },
  
  metricValue: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.text.primary,
    marginBottom: spacing.sm,
    [theme.breakpoints.up('lg')]: {
      fontSize: typography.fontSize['3xl'],
    },
    [theme.breakpoints.up('xl')]: {
      fontSize: typography.fontSize['4xl'],
    },
  },
  
  metricChange: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    [theme.breakpoints.up('lg')]: {
      fontSize: typography.fontSize.base,
    },
    [theme.breakpoints.up('xl')]: {
      fontSize: typography.fontSize.lg,
    },
  },
  positive: {
    color: colors.success.main,
  },
  negative: {
    color: colors.error.main,
  },
  neutral: {
    color: colors.text.secondary,
  },
  progressContainer: {
    marginTop: spacing.md,
  },
  progressBar: {
    height: '8px',
    borderRadius: borderRadius.base,
    backgroundColor: colors.background.tertiary,
    '& .MuiLinearProgress-bar': {
      borderRadius: borderRadius.base,
    },
  },
  marketDominance: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
    '& span': {
      color: colors.text.secondary,
      fontSize: typography.fontSize.sm,
      [theme.breakpoints.up('lg')]: {
        fontSize: typography.fontSize.base,
      },
    },
  },
  riskIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
    padding: spacing.sm,
    borderRadius: borderRadius.lg,
    background: 'rgba(255, 255, 255, 0.05)',
    [theme.breakpoints.up('lg')]: {
      padding: spacing.md,
    },
  },
  alert: {
    backgroundColor: '#ff444420',
    border: '1px solid #ff4444',
    color: '#ff4444',
  },
}));

const TradingDashboard = () => {
  const [globalData, setGlobalData] = useState(null);
  const [marketTrends, setMarketTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const classes = useStyles();

  useEffect(() => {
    fetchGlobalData();
  }, []);

  const fetchGlobalData = async () => {
    try {
      setLoading(true);
      const { data } = await fetchWithRetry(GlobalData());
      setGlobalData(data.data);
      
      // Simulate market trends data (in real app, this would come from API)
      setMarketTrends({
        btcDominance: data.data.market_cap_percentage.btc,
        ethDominance: data.data.market_cap_percentage.eth,
        totalVolume24h: data.data.total_volume.usd,
        marketCapChange24h: data.data.market_cap_change_percentage_24h,
        activeCoins: data.data.active_cryptocurrencies,
        totalMarkets: data.data.markets,
      });
    } catch (error) {
      const errorInfo = handleApiError(error, 'Global data fetch');
      console.error('Failed to fetch global data:', errorInfo);
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevel = (volatility) => {
    if (volatility > 50) return { level: 'High', color: colors.error.main, icon: <Warning /> };
    if (volatility > 25) return { level: 'Medium', color: colors.warning.main, icon: <Info /> };
    return { level: 'Low', color: colors.success.main, icon: <Security /> };
  };

  const getMarketSentiment = (change) => {
    if (change > 5) return { sentiment: 'Bullish', color: colors.success.main, icon: <TrendingUp /> };
    if (change < -5) return { sentiment: 'Bearish', color: colors.error.main, icon: <TrendingDown /> };
    return { sentiment: 'Neutral', color: colors.text.secondary, icon: <ShowChart /> };
  };

  if (loading) {
    return (
      <div className={classes.dashboard}>
        <Container>
          <LinearProgress style={{ backgroundColor: colors.background.tertiary }} />
        </Container>
      </div>
    );
  }

  if (!globalData || !marketTrends) {
    return (
      <div className={classes.dashboard}>
        <Container>
          <Alert severity="error">Failed to load market data</Alert>
        </Container>
      </div>
    );
  }

  const riskLevel = getRiskLevel(Math.abs(marketTrends.marketCapChange24h));
  const marketSentiment = getMarketSentiment(marketTrends.marketCapChange24h);

  return (
    <div className={classes.dashboard}>
      <Container className={classes.container}>
        <Typography variant="h5" className={classes.title}>
          Market Overview & Analytics
        </Typography>

        <Grid container spacing={3}>
          {/* Market Cap Overview */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.card}>
              <CardContent>
                <div className={classes.cardHeader}>
                  <Typography variant="h6">Total Market Cap</Typography>
                  <AttachMoney style={{ color: colors.primary.main }} />
                </div>
                <Typography className={classes.metricValue}>
                  {formatLargeNumber(globalData.total_market_cap.usd)}
                </Typography>
                <Typography 
                  className={`${classes.metricChange} ${getColorByValue(marketTrends.marketCapChange24h) === colors.success.main ? classes.positive : classes.negative}`}
                >
                  {formatPercentage(marketTrends.marketCapChange24h)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* 24h Volume */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.card}>
              <CardContent>
                <div className={classes.cardHeader}>
                  <Typography variant="h6">24h Volume</Typography>
                  <Speed style={{ color: colors.primary.main }} />
                </div>
                <Typography className={classes.metricValue}>
                  {formatLargeNumber(marketTrends.totalVolume24h)}
                </Typography>
                <Typography className={classes.neutral}>
                  Active trading volume
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Market Sentiment */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.card}>
              <CardContent>
                <div className={classes.cardHeader}>
                  <Typography variant="h6">Market Sentiment</Typography>
                  {marketSentiment.icon}
                </div>
                <Typography className={classes.metricValue} style={{ color: marketSentiment.color }}>
                  {marketSentiment.sentiment}
                </Typography>
                <Typography className={classes.neutral}>
                  Based on 24h change
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Risk Level */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.card}>
              <CardContent>
                <div className={classes.cardHeader}>
                  <Typography variant="h6">Risk Level</Typography>
                  {riskLevel.icon}
                </div>
                <Typography className={classes.metricValue} style={{ color: riskLevel.color }}>
                  {riskLevel.level}
                </Typography>
                <Typography className={classes.neutral}>
                  Volatility indicator
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Bitcoin Dominance */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" style={{ marginBottom: '15px' }}>
                  Market Dominance
                </Typography>
                
                <div className={classes.marketDominance}>
                  <span>Bitcoin (BTC)</span>
                  <span>{marketTrends.btcDominance.toFixed(1)}%</span>
                </div>
                <LinearProgress 
                  variant="determinate" 
                  value={marketTrends.btcDominance} 
                  className={classes.progressBar}
                  style={{ backgroundColor: colors.background.tertiary }}
                />

                <div className={classes.marketDominance}>
                  <span>Ethereum (ETH)</span>
                  <span>{marketTrends.ethDominance.toFixed(1)}%</span>
                </div>
                <LinearProgress 
                  variant="determinate" 
                  value={marketTrends.ethDominance} 
                  className={classes.progressBar}
                  style={{ backgroundColor: colors.background.tertiary }}
                />

                <div className={classes.marketDominance}>
                  <span>Others</span>
                  <span>{(100 - marketTrends.btcDominance - marketTrends.ethDominance).toFixed(1)}%</span>
                </div>
                <LinearProgress 
                  variant="determinate" 
                  value={100 - marketTrends.btcDominance - marketTrends.ethDominance} 
                  className={classes.progressBar}
                  style={{ backgroundColor: colors.background.tertiary }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Market Statistics */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" style={{ marginBottom: '15px' }}>
                  Market Statistics
                </Typography>
                
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" style={{ color: colors.text.secondary }}>
                    Active Cryptocurrencies
                  </Typography>
                  <Chip 
                    label={marketTrends.activeCoins} 
                    size="small"
                    style={{ backgroundColor: colors.primary.main, color: '#fff' }}
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" style={{ color: colors.text.secondary }}>
                    Total Markets
                  </Typography>
                  <Chip 
                    label={marketTrends.totalMarkets} 
                    size="small"
                    style={{ backgroundColor: colors.success.main, color: '#000' }}
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" style={{ color: colors.text.secondary }}>
                    Market Cap Change (24h)
                  </Typography>
                  <Chip 
                    label={formatPercentage(marketTrends.marketCapChange24h)}
                    size="small"
                    className={getColorByValue(marketTrends.marketCapChange24h) === colors.success.main ? classes.positive : classes.negative}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Trading Insights */}
          <Grid item xs={12}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" style={{ marginBottom: '15px' }}>
                  Professional Trading Insights
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box className={classes.riskIndicator}>
                      <Warning style={{ color: colors.warning.main }} />
                      <Typography variant="body2" style={{ color: colors.text.secondary }}>
                        High volatility periods often present the best trading opportunities
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box className={classes.riskIndicator}>
                      <TrendingUp style={{ color: colors.success.main }} />
                      <Typography variant="body2" style={{ color: colors.text.secondary }}>
                        Volume/Market Cap ratio above 5% indicates high trading activity
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box className={classes.riskIndicator}>
                      <Security style={{ color: colors.primary.main }} />
                      <Typography variant="body2" style={{ color: colors.text.secondary }}>
                        RSI above 70 suggests overbought conditions, below 30 oversold
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default TradingDashboard; 